import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Button, FormGroup, FormLabel, Grid, IconButton, MenuItem, Paper, Stack, Typography } from '@mui/material';
import ServiceFormCalendarItem from 'app/calendarV4/services/serviceForm/ServiceFormCalendarItem';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import useColorsStyles from 'common/colors';
import ActionButton from 'common/components/ActionButton';
import RoundedSelect from 'common/components/RoundedSelect';
import useFlexBoxStyles from 'common/flexBox';
import { useSettingValue } from 'common/hooks/useSettingValue';
import LayoutForm from 'common/layouts/LayoutForm';
import useOnliveStyles from 'common/onliveStyles';
import { customMenuPropsRight } from 'common/utils/menuProps';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { useFormFields } from 'libs/hooksLib.js';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { fetchAvailabilitySlotsAction } from 'redux/slices/iCalendarV4/availabilitySlice';
import { closeNestedDialogAction } from 'redux/slices/nestedDialogSlice';
import { makeStyles } from 'tss-react/mui';
import SelectByExtraField from '../filters/SelectByExtraField';

const useStyles = makeStyles()((theme) => ({
    calendarGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: theme.spacing(1),
        fontSize: theme.spacing(1.25),
    },
    dayCell: {
        padding: theme.spacing(1),
        textAlign: 'center',
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
    },
    dayDisabled: {
        opacity: 0.3,
        cursor: 'not-allowed',
    },
    dayAvailable: {
        backgroundColor: theme.palette.action.hover,
    },
    daySelected: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    slot: {
        fontSize: theme.spacing(1.25),
    },
    slotSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.selected,
    },
    filter: {
        '& > *': {
            marginTop: theme.spacing(0.5) + ' !important',
            marginBottom: theme.spacing(0.5) + ' !important',
        },
    },
    w20: {
        width: theme.spacing(20),
    },
}));

export default function AppointmentFormAvailability({ serviceId, onConfirm }) {
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();
    const { classes: colors } = useColorsStyles();
    const { classes: onlive } = useOnliveStyles();

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const initialForm = useMemo(() => {
        return {
            date: '',
            startTime: '',
            endTime: '',
            agentId: '',
            assetId: '',
        };
    }, []);
    const [fields, handleFieldChange, resetForm] = useFormFields(initialForm);
    const today = new Date();
    const [month, setMonth] = useState(today.getMonth());
    const [year, setYear] = useState(today.getFullYear());
    const [selectedSlot, setSelectedSlot] = useState(null);
    const timezone = useSettingValue(SettingsKey.TIMEZONE) || 'Europe/Madrid';
    const [availability, setAvailability] = useState([]);
    const [model, setModel] = useState('all');
    const [asset, setAsset] = useState('all');
    const [agent, setAgent] = useState('all');
    const dateSlotMap = availability?.dateSlotMap ?? {};

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();

    const days = useMemo(() => {
        const result = [];
        for (let i = 0; i < firstDay; i++) result.push(null);
        for (let d = 1; d <= daysInMonth; d++) result.push(d);
        return result;
    }, [daysInMonth, firstDay]);

    const dateKey = (day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    const slots = fields.date ? dateSlotMap[fields.date] || [] : [];

    const calendarsForSlot = useMemo(() => {
        if (!selectedSlot) return [];
        return selectedSlot.calendars.map((i) => availability.calendars[i]);
    }, [selectedSlot, availability]);

    const handlePrevMonth = () => {
        setMonth((m) => {
            if (m === 0) {
                setYear((y) => y - 1);
                return 11;
            }
            return m - 1;
        });
    };

    const handleNextMonth = () => {
        setMonth((m) => {
            if (m === 11) {
                setYear((y) => y + 1);
                return 0;
            }
            return m + 1;
        });
    };

    const disableSave = useMemo(() => !fields.agentId || !fields.assetId, [fields]);

    useEffect(() => {
        const agent =
            calendarsForSlot.find((c) => fields.agentId === c.id) || calendarsForSlot.find((c) => c.type === 'agent');
        if (agent?.id !== fields.agentId) {
            handleFieldChange({
                target: {
                    value: agent?.id || '',
                    name: 'agentId',
                },
            });
        }
        const asset =
            calendarsForSlot.find((c) => fields.assetId === c.id) || calendarsForSlot.find((c) => c.type === 'asset');
        if (asset?.id !== fields.assetId) {
            handleFieldChange({
                target: {
                    value: asset?.id || '',
                    name: 'assetId',
                },
            });
        }
    }, [calendarsForSlot, fields.agentId, fields.assetId, handleFieldChange]);

    useEffect(() => {
        if (timezone) {
            setSelectedSlot(null);
            handleFieldChange({
                target: {
                    value: '',
                    name: 'agentId',
                },
            });
            handleFieldChange({
                target: {
                    value: '',
                    name: 'assetId',
                },
            });
            const baseDate = new Date(year, month, 1);
            const fromDate = format(startOfMonth(baseDate), 'yyyy-MM-dd');
            const toDate = format(endOfMonth(baseDate), 'yyyy-MM-dd');
            const calendars = [
                { type: 'agent', ...(agent === 'all' ? {} : { id: agent }) },
                { type: 'asset', ...(model === 'all' ? {} : { model }), ...(asset === 'all' ? {} : { id: asset }) },
            ];
            dispatch(
                fetchAvailabilitySlotsAction({
                    queryParams: {
                        serviceId,
                        fromDate,
                        toDate,
                        timezone,
                        calendars,
                        bookingSlots: true,
                    },
                    onSuccess: (data) => {
                        setAvailability(data);
                    },
                }),
            );
        }
    }, [dispatch, month, year, serviceId, timezone, agent, model, asset, resetForm, initialForm, handleFieldChange]);

    useEffect(() => {
        handleFieldChange({
            target: {
                value: selectedSlot?.startTime || null,
                name: 'startTime',
            },
        });
        handleFieldChange({
            target: {
                value: selectedSlot?.endTime || null,
                name: 'endTime',
            },
        });
    }, [selectedSlot, handleFieldChange]);

    const handleSave = () => {
        if (!selectedSlot) return;
        const { date, startTime, endTime, agentId, assetId } = fields;
        onConfirm({
            startAt: new Date(`${date} ${startTime}`),
            endAt: new Date(`${date} ${endTime}`),
            calendars: calendarsForSlot
                .filter((c) => c.id === agentId || c.id === assetId)
                .map((c) => ({ ...c, canonicalId: c.id })),
        });
        dispatch(closeNestedDialogAction());
    };

    const cancel = (event) => {
        event.preventDefault();
        dispatch(closeNestedDialogAction());
    };

    return (
        <LayoutForm title={t('iCalendar.appointmentForm.findAvailability')} customClasses={flexBox.vH70}>
            <Stack spacing={2} className={cx(flexBox.h100, flexBox.overflowHidden)}>
                <Stack direction="column" className={cx(flexBox.h100, flexBox.scrollbarNone, flexBox.overflowAuto)}>
                    <Stack
                        spacing={2}
                        className={cx(
                            colors.bgNeutral100,
                            onlive.borderMediumGray,
                            flexBox.padding1,
                            flexBox.borderRadius2,
                        )}
                    >
                        {/* Calendar column */}
                        <Stack
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="center"
                            spacing={2}
                            className={cx(flexBox.padding10, flexBox.w100, flexBox.wrap, classes.filter)}
                        >
                            <SelectByExtraField
                                field="model"
                                customMenuProps={customMenuPropsRight}
                                onSubmit={(e) => {
                                    setModel(e.target.value);
                                    setAsset('all');
                                }}
                                size={'smallWidth'}
                                fullWidth={false}
                                fieldParamId={model}
                            />

                            <RoundedSelect
                                bold={true}
                                name="asset"
                                value={asset}
                                onChange={(e) => setAsset(e.target.value)}
                                fullWidth={false}
                                size="smallWidth"
                            >
                                <MenuItem value="all">{t('iCalendar.filter.allAssets')}</MenuItem>
                                {availability?.calendars
                                    ?.filter((c) => c.type === 'asset')
                                    .map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c?.name +
                                                (c?.extraFields?.resourceId
                                                    ? ' (' + c?.extraFields?.resourceId + ')'
                                                    : '')}
                                        </MenuItem>
                                    ))}
                            </RoundedSelect>

                            <RoundedSelect
                                bold={true}
                                name="agent"
                                value={agent}
                                onChange={(e) => setAgent(e.target.value)}
                                fullWidth={false}
                                size="smallWidth"
                            >
                                <MenuItem value="all">{t('iCalendar.filter.allAgents')}</MenuItem>
                                {availability?.calendars
                                    ?.filter((c) => c.type === 'agent')
                                    .map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c?.name + (c?.email ? ' (' + c?.email + ')' : '')}
                                        </MenuItem>
                                    ))}
                            </RoundedSelect>
                        </Stack>
                        <Stack direction="row" spacing={3}>
                            <Stack spacing={2} sx={{ width: '50%' }}>
                                <Stack direction="row" alignItems="center">
                                    <IconButton size="small" onClick={handlePrevMonth}>
                                        <ChevronLeftIcon />
                                    </IconButton>

                                    <Typography variant="h8" sx={{ flexGrow: 1, textAlign: 'center' }}>
                                        {new Date(year, month).toLocaleString('en-US', {
                                            month: 'long',
                                            year: 'numeric',
                                        })}
                                    </Typography>

                                    <IconButton size="small" onClick={handleNextMonth}>
                                        <ChevronRightIcon />
                                    </IconButton>
                                </Stack>

                                <Paper variant="outlined" sx={{ p: 1 }}>
                                    <div className={classes.calendarGrid}>
                                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, index) => (
                                            <Typography
                                                key={d + '-' + index}
                                                variant="caption"
                                                textAlign="center"
                                                fontWeight={600}
                                                fontSize={10}
                                            >
                                                {d}
                                            </Typography>
                                        ))}

                                        {days.map((day, idx) => {
                                            if (!day) return <div key={idx} />;

                                            const key = dateKey(day);
                                            const hasAvailability = Boolean(dateSlotMap[key]);

                                            return (
                                                <div
                                                    key={idx}
                                                    className={cx(
                                                        classes.dayCell,
                                                        !hasAvailability && classes.dayDisabled,
                                                        hasAvailability && classes.dayAvailable,
                                                        fields.date === key && classes.daySelected,
                                                    )}
                                                    onClick={() => {
                                                        if (hasAvailability) {
                                                            handleFieldChange({ target: { name: 'date', value: key } });
                                                            setSelectedSlot(null);
                                                        }
                                                    }}
                                                >
                                                    {day}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Paper>
                            </Stack>

                            {/* Slots column */}
                            <Stack spacing={2} sx={{ width: '50%' }}>
                                {!fields.date && (
                                    <Paper variant="outlined" sx={{ p: 3 }}>
                                        <Typography color="text.secondary">
                                            {t('iCalendar.appointmentForm.selectAvailableTimeSlots')}
                                        </Typography>
                                    </Paper>
                                )}

                                {fields.date && (
                                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 250, overflowY: 'auto' }}>
                                        <Stack spacing={1}>
                                            {slots.map((slot, idx) => {
                                                const slotCalendars = slot.calendars.map(
                                                    (i) => availability.calendars[i],
                                                );
                                                const assetCount = slotCalendars.filter(
                                                    (c) => c.type === 'asset',
                                                ).length;
                                                const agentCount = slotCalendars.filter(
                                                    (c) => c.type === 'agent',
                                                ).length;

                                                return (
                                                    <Button
                                                        key={idx}
                                                        variant="outlined"
                                                        className={cx(selectedSlot === slot && classes.slotSelected)}
                                                        onClick={() => setSelectedSlot(slot)}
                                                    >
                                                        <Stack
                                                            direction="column"
                                                            justifyContent="space-between"
                                                            width="100%"
                                                            className={classes.slot}
                                                        >
                                                            <span>
                                                                {slot.startTime} – {slot.endTime}
                                                            </span>
                                                            <span>
                                                                {t('service.form.assets') +
                                                                    ': ' +
                                                                    assetCount +
                                                                    ' · ' +
                                                                    t('user.userStatistics.agents') +
                                                                    ': ' +
                                                                    agentCount}
                                                            </span>
                                                        </Stack>
                                                    </Button>
                                                );
                                            })}
                                        </Stack>
                                    </Paper>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>

                    <Grid container spacing={2} wrap="nowrap" sx={{ ml: -2 }} className={cx(flexBox.paddingTop2)}>
                        <Grid item xs={6}>
                            <Stack spacing={2}>
                                <FormGroup>
                                    <FormLabel>{t('app.common.agent')}</FormLabel>
                                    <RoundedSelect
                                        bold={true}
                                        name="agentId"
                                        value={fields?.agentId}
                                        onChange={handleFieldChange}
                                        fullWidth={true}
                                        size="big"
                                    >
                                        {calendarsForSlot
                                            ?.filter((c) => c.type === 'agent')
                                            .map((c) => (
                                                <MenuItem key={c.id} value={c.id}>
                                                    {c?.name + (c?.email ? ' (' + c?.email + ')' : '')}
                                                </MenuItem>
                                            ))}
                                    </RoundedSelect>
                                </FormGroup>

                                {fields?.agentId && (
                                    <Stack className={classes.w20}>
                                        <ServiceFormCalendarItem
                                            calendar={calendarsForSlot?.find((c) => c.id === fields?.agentId)}
                                        />
                                    </Stack>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={6}>
                            <Stack spacing={2}>
                                <FormGroup>
                                    <FormLabel>{t('app.common.asset')}</FormLabel>
                                    <RoundedSelect
                                        bold={true}
                                        name="assetId"
                                        value={fields?.assetId}
                                        onChange={handleFieldChange}
                                        fullWidth={true}
                                        size="big"
                                    >
                                        {calendarsForSlot
                                            ?.filter((c) => c.type === 'asset')
                                            .map((c) => (
                                                <MenuItem key={c.id} value={c.id}>
                                                    {c?.name +
                                                        (c?.extraFields?.resourceId
                                                            ? ' (' + c?.extraFields?.resourceId + ')'
                                                            : '')}
                                                </MenuItem>
                                            ))}
                                    </RoundedSelect>
                                </FormGroup>

                                {fields?.assetId && (
                                    <Stack className={classes.w20}>
                                        <ServiceFormCalendarItem
                                            calendar={calendarsForSlot?.find((c) => c.id === fields?.assetId)}
                                        />
                                    </Stack>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </Stack>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                <ActionButton onClick={cancel} color="default" fitWidth>
                    {t('multimedia.multimediaForm.cancel')}
                </ActionButton>

                <ActionButton color="primary" autoFocus fitWidth onClick={handleSave} disabled={disableSave}>
                    {t('app.common.save')}
                </ActionButton>
            </Stack>
        </LayoutForm>
    );
}
